import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import Baseball from '@/components/Baseball';
import RadioTower from '@/components/RadioTower';
import { playClick, playSuccess } from '@/utils/sounds';
import { 
  RetroBackground, 
  RetroCard, 
  RetroLogo, 
  RetroButton, 
  RetroInputField,
  RetroText,
  PALETTE,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const BASEBALL_QUOTES = [
  { quote: "Baseball is ninety percent mental. The other half is physical.", author: "Yogi Berra" },
  { quote: "Love is the most important thing in the world, but baseball is pretty good, too.", author: "Yogi Berra" },
  { quote: "Little League baseball is a very good thing because it keeps the parents off the streets.", author: "Yogi Berra" },
  { quote: "It's fun; baseball is fun.", author: "Yogi Berra" },
  { quote: "The game of baseball isn't over until it's over.", author: "Yogi Berra" },
  { quote: "This is like Deja vu all over again.", author: "Yogi Berra" },
  { quote: "Slump? I'm not in a slump. I'm just not hitting.", author: "Yogi Berra" },
  { quote: "If the people don't wanna come out to the ballpark, nobody's gonna stop 'em.", author: "Yogi Berra" },
  { quote: "Baseball ain't like football. You can't make up no trick plays.", author: "Yogi Berra" },
  { quote: "I'm ugly. So what? I've never seen anyone hit with his face.", author: "Yogi Berra" },
  { quote: "Think?! How are you supposed to think and hit at the same time?", author: "Yogi Berra" },
  { quote: "I never blame myself when I'm not hitting. I just blame the bat, and if it keeps up, I change bats.", author: "Yogi Berra" },
  { quote: "Baseball was, is, and always will be the best game in the world to me.", author: "Babe Ruth" },
  { quote: "Every strike brings me closer to the next home run.", author: "Babe Ruth" },
  { quote: "Never allow the fear of striking out keep you from playing the game.", author: "Babe Ruth" },
  { quote: "Every day is a new opportunity. You can build on yesterday's success or put its failures behind and start over again.", author: "Bullet Bob Feller" },
  { quote: "If my uniform doesn't get dirty, I haven't done anything in the baseball game.", author: "Rickey Henderson" },
  { quote: "People ask me what I do in the winter when there's no baseball. I stare out the window and wait for spring.", author: "Rogers Hornsby" },
  { quote: "Playing baseball for a living is like having a license to steal.", author: "Pete Rose" },
  { quote: "Fans don't boo nobodies.", author: "Reggie Jackson" },
  { quote: "One of the beautiful things about baseball is the history.", author: "James Anthony Abbott" },
  { quote: "For all its gentility, its almost leisurely pace, baseball is violence under wraps.", author: "Willie Mays" },
  { quote: "It's unbelievable how much you don't know about the game you've been playing your whole life.", author: "Mickey Mantle" },
  { quote: "They give you a round bat, and they throw you a round ball, and then they tell you to hit it square.", author: "Willie Stargell" },
  { quote: "When you start the game, they don't say 'Work Ball!' They say, 'Play Ball!'", author: "Willie Stargell" },
  { quote: "I never smile when I have a bat in my hands. That's when you've got to be serious.", author: "Hank Aaron" },
  { quote: "Nothing's ever been as fun as baseball.", author: "Mickey Mantle" },
  { quote: "A life is not important except in the impact it has on other lives.", author: "Jackie Robinson" },
  { quote: "I never had a job. I just always played baseball.", author: "Satchel Paige" },
  { quote: "A baseball game is simply a nervous breakdown divided into nine innings.", author: "Robert Earl Wilson" },
  { quote: "Remember these two things: play hard and have fun.", author: "Tony Gwynn" },
  { quote: "Baseball was 100% of my life.", author: "Ty Cobb" },
  { quote: "I am convinced that God wanted me to be a baseball player.", author: "Roberto Clemente" },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [randomQuote] = useState(() => 
    BASEBALL_QUOTES[Math.floor(Math.random() * BASEBALL_QUOTES.length)]
  );
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    playClick();
    setLoading(true);
    
    try {
      await login(email, password);
      playSuccess();
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RetroBackground variant="cream" className="flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo with Radio Tower and Baseball */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center gap-4 mb-6">
            <RadioTower size={70} animated={true} />
            <Baseball size={80} />
          </div>
          
          <RetroLogo size="lg" className="mb-6" />
          
          {/* Quote - UI font for body text */}
          <div className="max-w-sm mx-auto">
            <p 
              className="italic mb-2"
              style={{ 
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: DESIGN_TOKENS.fontSizes.uiSm,
                lineHeight: '1.6',
                color: PALETTE.ink,
              }}
            >
              &quot;{randomQuote.quote}&quot;
            </p>
            <p style={{ 
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 600,
              fontSize: DESIGN_TOKENS.fontSizes.uiXs,
              color: PALETTE.dirt,
            }}>
              - {randomQuote.author}
            </p>
          </div>
        </div>

        {/* Login Form Card */}
        <RetroCard title="SIGN IN" className="mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <RetroInputField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="player@example.com"
              required
              data-testid="login-email-input"
            />
            
            <RetroInputField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="login-password-input"
            />
            
            <RetroButton
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'LOADING...' : 'PLAY BALL!'}
            </RetroButton>
          </form>
          
          <div className="mt-6 text-center">
            <RetroText size="sm" color="ink">
              New player?{' '}
            </RetroText>
            <Link 
              to="/register" 
              className="hover:underline"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontWeight: 600,
                fontSize: DESIGN_TOKENS.fontSizes.uiSm,
                color: PALETTE.grass,
              }}
              data-testid="register-link"
            >
              Create Account
            </Link>
          </div>
        </RetroCard>
        
        {/* Footer Links */}
        <div className="text-center space-y-3">
          <Link 
            to="/how-to-play" 
            className="block hover:underline"
            style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 600,
              fontSize: DESIGN_TOKENS.fontSizes.uiSm,
              color: PALETTE.grass,
            }}
            data-testid="how-to-play-link"
          >
            How to Play
          </Link>
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: DESIGN_TOKENS.fontSizes.uiXs,
            color: PALETTE.dirt,
          }}>
            Powered by Apple Music
          </p>
        </div>
      </div>
    </RetroBackground>
  );
}
